/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { FunctionComponent, VNode } from 'preact';
import {
  HTMLAttributes,
  Children,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useLayoutEffect,
} from 'preact/compat';
import { classes, debounce } from '@adobe-commerce/elsie/lib';
import { Button, Icon } from '@adobe-commerce/elsie/components';
import { ChevronDown } from '@adobe-commerce/elsie/icons';
import { useText } from '@adobe-commerce/elsie/i18n';

import '@/pdp/components/Carousel/Carousel.css';

type Show = number;

const PEAK_WIDTH = 24;
const THUMB_GAP = 16;
const ICON_SIZE = '32';

enum GapSize {
  small = 16,
  medium = 24,
  large = 64,
}

export interface CarouselProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'controls'> {
  children: VNode[] | VNode;
  thumbnails?: VNode[] | VNode;
  show?: Show | { small: Show; medium: Show; large: Show };
  gap?: 'small' | 'medium' | 'large' | null;
  scrollbar?: boolean;
  peak?: boolean;
  arrows?: boolean;
  controls?: 'thumbnailsRow' | 'thumbnailsColumn' | 'dots' | null;
  arrowsOnMainImage?: boolean;
  loop?: boolean;
  direction?: 'horizontal';
  style?: Record<string, string | number>;
  width?: string;
  height?: string;
  defaultIndex?: number;
  infinite?: boolean;
  isZoomed?: boolean;
}

export const Carousel: FunctionComponent<CarouselProps> = ({
  show = 1,
  scrollbar = false,
  peak = false,
  arrows = false,
  controls = 'dots',
  arrowsOnMainImage = false,
  loop = false,
  gap = null,
  direction = 'horizontal',
  width = '100%',
  height = '100%',
  defaultIndex = 0,
  className,
  children,
  thumbnails = null,
  isZoomed,
  ...props
}) => {
  // i18n
  const Label = useText('PDP.Carousel.label').label;
  const SlideLabel = useText('PDP.Carousel.Slide.label').label;
  const PreviousLabel = useText('PDP.Carousel.Previous.label').label;
  const NextLabel = useText('PDP.Carousel.Next.label').label;
  const ControlsLabel = useText('PDP.Carousel.Controls.label').label;
  const ControlsBtnLabel = useText('PDP.Carousel.Controls.Button.label').label;

  // States
  const [perPage, setPerPage] = useState(() => getPerPageFromBreakpoint(show));

  // Constants
  const length = Math.ceil(Children.count(children) / perPage);

  const peakWidth = peak ? PEAK_WIDTH : 0;
  const gapWidth = gap ? GapSize[gap] : 0;
  const rest = Children.count(children) % perPage;

  // States
  const [current, setCurrent] = useState(
    loop ? (isRTL() ? defaultIndex - 1 : defaultIndex + 1) : defaultIndex
  );

  // Thumbnails (refs for each thumbnail item)
  const thumbnailsRefs = useRef<HTMLLabelElement[]>([]);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(0);

  // VNodes
  const thumbnailSlides = useMemo<VNode[]>(() => {
    return Children.toArray(thumbnails);
  }, [thumbnails]);

  // Slides
  const slides = useMemo<VNode[][]>(() => {
    const returnValue = Children.toArray(children).reduce((acc, child, i) => {
      if (i % perPage === 0) {
        return [...acc, [child]];
      }

      return [...acc.slice(0, -1), [...acc[acc.length - 1], child]];
    }, [] as VNode[][]);

    if (!loop || returnValue.length === 1) {
      return returnValue;
    }

    if (loop && !(Children.count(children) % perPage !== 0)) {
      return [returnValue[length - 1]]
        .concat(returnValue)
        .concat([returnValue[0], returnValue[1]]);
    }

    return [Children.toArray(children).slice(-perPage)]
      .concat(returnValue)
      .concat([returnValue[0], returnValue[1]]);
  }, [children, perPage, length, loop]);

  // Wrapper
  const wrapperRef = useRef<HTMLDivElement>(null);
  const thumbnailsWrapperRef = useRef<HTMLDivElement>(null);

  const thumbnailControls =
    controls === 'thumbnailsRow' || controls === 'thumbnailsColumn';

  // Observe Resize (responsive)
  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) return; // Guard clause in case the ref is not set yet

    const handleResize = debounce(() => {
      setThumbWidth(wrapper.offsetWidth ?? 0);
      setThumbHeight(wrapper.offsetHeight ?? 0);
      const width = window.innerWidth;
      const newPerPage = getPerPageFromBreakpoint(show, width);

      if (newPerPage !== perPage) {
        setPerPage(newPerPage);
      }
    }, 50);

    const observer = new ResizeObserver(handleResize);
    observer.observe(wrapper);

    return () => {
      observer.unobserve(wrapper);
    };
  }, [perPage, show]);

  // Scrolling logic controls
  const handleThumbnailsScroll = useCallback(
    (nextSlideIndex: number) => {
      const nextItem = loop ? nextSlideIndex - 1 : nextSlideIndex;
      const thumbnailRef = thumbnailsRefs?.current[0];
      const oneItemWidth = thumbnailRef?.offsetWidth + THUMB_GAP;
      const oneItemHeight = thumbnailRef?.offsetHeight + THUMB_GAP;
      const thumbnailsWrapper = thumbnailsWrapperRef.current!;

      const isRow = controls === 'thumbnailsRow';
      const isColumn = controls === 'thumbnailsColumn';
      const offset = isRow
        ? thumbnailsWrapper?.offsetWidth
        : thumbnailsWrapper?.offsetHeight;
      const oneItemSize = isRow ? oneItemWidth : oneItemHeight;

      if ((isRow || isColumn) && offset) {
        const visibleThumbnails = Math.floor(
          (isRow
            ? thumbnailsWrapper.clientWidth
            : thumbnailsWrapper.clientHeight) / oneItemSize
        );
        const centerOffset = Math.floor(visibleThumbnails / 2);
        const targetScrollPosition = (nextItem - centerOffset) * oneItemSize;
        thumbnailsWrapper.style.scrollBehavior = 'smooth';

        if (isRow) {
          thumbnailsWrapper.scrollLeft = isRTL()
            ? -(
                thumbnailsWrapper.scrollWidth -
                thumbnailsWrapper.clientWidth -
                targetScrollPosition
              )
            : targetScrollPosition;
        } else {
          thumbnailsWrapper.scrollTop = targetScrollPosition;
        }
      }
    },
    [controls, loop]
  );

  useEffect(() => {
    handleThumbnailsScroll(current);
  }, [current, handleThumbnailsScroll]);

  // Navigation
  const handleScrollTo = useCallback(
    (nextSlideIndex: number, smooth?: boolean, reset?: boolean) => {
      if (thumbnailControls) {
        handleThumbnailsScroll(nextSlideIndex);
      }

      const wrapper = wrapperRef.current!;
      const realWidth = peakWidth
        ? wrapper.offsetWidth - peakWidth
        : wrapper.offsetWidth - peakWidth + gapWidth;

      const items = Array.from(wrapper.querySelectorAll('[data-index]')).filter(
        (element) => {
          const dataIndex = Number(element.getAttribute('data-index')!);
          return !isNaN(dataIndex) && dataIndex > -1;
        }
      );

      const item = items[nextSlideIndex];

      if (!item) {
        return;
      }

      if (reset) {
        const futureIndex = nextSlideIndex === 0 ? length : 0;
        const scrollWidth =
          realWidth * (length + (rest ? rest : perPage) / perPage);

        if (isRTL()) {
          if (
            wrapper.scrollLeft <= 0 &&
            wrapper.scrollLeft > -(realWidth - 5)
          ) {
            handleScrollTo(1, true);
            return;
          }
          if (
            wrapper.scrollLeft < realWidth - scrollWidth &&
            wrapper.scrollLeft >= -scrollWidth
          ) {
            handleScrollTo(length, true);
            return;
          }

          wrapper.style.scrollBehavior = 'auto';
          wrapper.scrollLeft = futureIndex === 0 ? 0 : -scrollWidth;
          handleScrollTo(futureIndex === 0 ? 1 : length, true);
        } else {
          if (wrapper.scrollLeft >= 0 && wrapper.scrollLeft < realWidth - 5) {
            handleScrollTo(1, true);
            return;
          }

          if (
            wrapper.scrollLeft > scrollWidth - realWidth &&
            wrapper.scrollLeft <= scrollWidth
          ) {
            handleScrollTo(length, true);
            return;
          }

          wrapper.style.scrollBehavior = 'auto';
          wrapper.scrollLeft = futureIndex === 0 ? 0 : scrollWidth;
          handleScrollTo(futureIndex === 0 ? 1 : length, true);
          return;
        }
      }

      const diff =
        ((wrapper.offsetWidth - gapWidth) / perPage) *
          slides[nextSlideIndex].length -
        peakWidth;

      wrapper.style.scrollBehavior = smooth ? 'smooth' : 'auto';
      wrapper.scrollLeft = isRTL()
        ? -(slides[nextSlideIndex].length === perPage
            ? realWidth * nextSlideIndex
            : realWidth * (nextSlideIndex - 1) - diff)
        : slides[nextSlideIndex].length === perPage
        ? realWidth * nextSlideIndex
        : realWidth * (nextSlideIndex - 1) + diff;
      return;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      gapWidth,
      length,
      peakWidth,
      perPage,
      rest,
      slides,
      handleThumbnailsScroll,
      controls,
    ]
  );

  const handleGoToPrev = useCallback(() => {
    if (loop) {
      const nextSlideIndex = current - 1;
      handleScrollTo(
        nextSlideIndex,
        true,
        isRTL() ? current === length - 1 : current === 1
      );
      return;
    }

    const nextSlideIndex = current <= 0 ? 0 : current - 1;
    handleScrollTo(nextSlideIndex, true);
  }, [current, handleScrollTo, loop]); //eslint-disable-line react-hooks/exhaustive-deps

  const handleGoToNext = useCallback(() => {
    const lastSlideIndex = slides.length - 1;

    if (loop) {
      const nextSlideIndex = current + 1;
      handleScrollTo(
        nextSlideIndex,
        true,
        isRTL() ? current === 0 : current === length
      );
      return;
    }

    const nextSlideIndex =
      current >= lastSlideIndex ? lastSlideIndex : current + 1;

    handleScrollTo(nextSlideIndex, true);
  }, [slides.length, loop, current, handleScrollTo, length]);

  // Navigate with keyboard
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        isRTL() ? handleGoToNext() : handleGoToPrev();
      }

      if (event.key === 'ArrowRight') {
        isRTL() ? handleGoToPrev() : handleGoToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleGoToNext, handleGoToPrev]);

  // Set default index
  useLayoutEffect(() => {
    handleScrollTo(loop ? defaultIndex + 1 : defaultIndex, false);
  }, [defaultIndex, handleScrollTo, loop, thumbWidth]);

  // Track scrolling (swipes)
  const handleOnScroll = debounce(() => {
    const wrapper = wrapperRef.current!;
    const realWidth = peakWidth
      ? wrapper.offsetWidth - peakWidth
      : wrapper.offsetWidth - peakWidth + gapWidth;

    const dividing = Math.abs(wrapper.scrollLeft) / realWidth;
    const nextSlideIndex = Math.abs(
      Math.round(dividing) - dividing < 1
        ? Math.round(dividing)
        : Math.ceil(dividing)
    );

    if (thumbnailControls) {
      handleThumbnailsScroll(nextSlideIndex);
    }

    if (loop) {
      const totalWidth =
        realWidth * (length + (rest ? rest : perPage) / perPage);
      const scrollOffset = isRTL() ? -wrapper.scrollLeft : wrapper.scrollLeft;

      if (Math.ceil(Math.abs(scrollOffset)) >= Math.ceil(totalWidth) - 5) {
        wrapper.style.scrollBehavior = 'auto';
        wrapper.scrollLeft = isRTL()
          ? -wrapper.offsetWidth +
            (Math.ceil(scrollOffset) - Math.ceil(totalWidth) - peakWidth)
          : wrapper.offsetWidth +
            (Math.ceil(scrollOffset) - Math.ceil(totalWidth) - peakWidth);
        return;
      }

      if (Math.abs(scrollOffset) === 0) {
        wrapper.style.scrollBehavior = 'auto';

        wrapper.scrollLeft = isRTL()
          ? -(totalWidth - wrapper.offsetWidth + peakWidth)
          : totalWidth - wrapper.offsetWidth + peakWidth;
        return;
      }
    }

    if (current !== nextSlideIndex) {
      setCurrent(nextSlideIndex);
    }
  }, 100);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    wrapper?.addEventListener('scroll', handleOnScroll);

    return () => {
      wrapper?.removeEventListener('scroll', handleOnScroll);
    };
  }, [handleOnScroll]);

  // thumbnails is column -> container is row reverse, controls is column
  // thumbnails is row -> container is column, controls is row
  const getArrow = (
    forThumbnails: boolean,
    direction: string,
    callback: () => void
  ) => {
    return (
      <Button
        className={classes([
          ['pdp-carousel__button', !forThumbnails],
          [`pdp-carousel__button--${direction}`, !forThumbnails],
          [`pdp-carousel__button--${controls}`, forThumbnails],
        ])}
        style={{
          '--height': `${thumbHeight}px`,
        }}
        variant="tertiary"
        aria-label={direction === 'next' ? NextLabel : PreviousLabel}
        onClick={callback}
        disabled={
          loop
            ? false
            : direction === 'next'
            ? current >= slides.length - 1
            : current < 1
        }
      >
        <Icon
          className={classes([
            ['pdp-carousel__button__icon', !forThumbnails],
            [`pdp-carousel__button__icon--${direction}`, !forThumbnails],
            [
              `pdp-carousel__button__icon--${controls}--${direction}`,
              forThumbnails,
            ],
          ])}
          size={ICON_SIZE}
          source={ChevronDown}
        />
      </Button>
    );
  };

  const prevArrow =
    arrows &&
    slides.length != 1 &&
    getArrow(thumbnailControls, 'prev', handleGoToPrev);

  const nextArrow =
    arrows &&
    slides.length != 1 &&
    getArrow(thumbnailControls, 'next', handleGoToNext);

  const prevArrowMainImage =
    arrows && slides.length != 1 && getArrow(false, 'prev', handleGoToPrev);

  const nextArrowMainImage =
    arrows && slides.length != 1 && getArrow(false, 'next', handleGoToNext);

  return (
    <div
      role="region"
      aria-roledescription={Label}
      className={classes([
        'pdp-carousel',
        ['pdp-carousel--main-image-controls', arrowsOnMainImage],
        ['pdp-carousel--arrows', arrows && !thumbnailControls],
        [`pdp-carousel--${controls}`, thumbnailControls],
        className,
      ])}
      style={{
        '--flex-carousel':
          controls === 'thumbnailsColumn' ? 'row-reverse' : 'column',
        '--gap': thumbnailControls ? '0' : 'var(--spacing-small)',
        '--width': width,
      }}
      {...props}
    >
      {/* Slideshow */}
      <div
        ref={wrapperRef}
        className={classes([
          'pdp-carousel__wrapper',
          `pdp-carousel__wrapper--${direction}`,
          ['pdp-carousel__wrapper--scrollbar', scrollbar],
          ['pdp-carousel__wrapper--peak', peak],
        ])}
        style={{
          '--total-width': controls === 'thumbnailsColumn' ? `81.6%` : `100%`,
          '--height': controls === 'thumbnailsColumn' ? 'auto' : height,
          '--gap': gap ? `var(--spacing-${gap})` : '0px',
          '--per-page': perPage,
        }}
        tabIndex={0}
      >
        {slides.map((page, index) => {
          const orphan = page.length < perPage;

          return (
            <div
              key={index}
              role="group"
              aria-roledescription={SlideLabel}
              data-index={index}
              className={classes([
                'pdp-carousel__slide',
                `pdp-carousel__slide--${direction}`,
                ['pdp-carousel__slide--active', current === index],
                ['pdp-carousel__slide--orphan', orphan],
                ['pdp-carousel__slide--hidden', thumbWidth === 0],
              ])}
              style={{
                '--length': page.length,
              }}
            >
              {page}
            </div>
          );
        })}
      </div>

      {/* Previous and next arrows for Carousel without thumbnails */}
      {!thumbnailControls && (
        <>
          {prevArrow}
          {nextArrow}
        </>
      )}

      {thumbnailControls && arrowsOnMainImage && (
        <>
          {prevArrowMainImage}
          {nextArrowMainImage}
        </>
      )}

      {/* Controls */}
      {controls && slides.length != 1 && (
        <div
          className={classes([
            [
              `pdp-carousel__controls__container--${controls}`,
              thumbnailControls,
            ],
            [
              'pdp-carousel__controls__container--no-arrows',
              !arrows || arrowsOnMainImage,
            ],
          ])}
          style={{
            '--width': controls === 'thumbnailsRow' ? `${thumbWidth}px` : width,
            '--height': `${thumbHeight}px`,
            '--nr-thumbnails': thumbnailSlides.length,
          }}
        >
          {(thumbnailControls || isZoomed) && !arrowsOnMainImage && prevArrow}

          <div
            ref={thumbnailsWrapperRef}
            className={classes([
              [
                `pdp-carousel__controls__wrapper--${controls}`,
                thumbnailControls,
              ],
              [`pdp-carousel__controls__wrapper`, thumbnailControls],
            ])}
          >
            <div
              role="group"
              aria-label={ControlsLabel}
              className={classes([
                'pdp-carousel__controls',
                [`pdp-carousel__controls--${controls}`, thumbnailControls],
              ])}
            >
              {(loop ? slides.slice(0, length) : slides).map((_, key) => {
                const buttonsLength = loop ? length : slides.length;
                const active = loop ? current - 1 === key : current === key;

                if (!thumbnailControls) {
                  return (
                    <button
                      key={key}
                      aria-label={ControlsBtnLabel.replace(
                        '{key}',
                        String(key + 1)
                      ).replace('{total}', String(buttonsLength))}
                      onClick={() => {
                        handleScrollTo(loop ? key + 1 : key, true);
                      }}
                      className={classes([
                        'pdp-carousel__controls__button',
                        ['pdp-carousel__controls__button--active', active],
                      ])}
                    />
                  );
                }
                return (
                  <label
                    className={classes(['pdp-carousel__thumbnail__container'])}
                    key={key}
                    ref={(el: HTMLLabelElement | null) => {
                      if (el) {
                        thumbnailsRefs.current[key] = el;
                      }
                    }}
                  >
                    <input
                      type="radio"
                      name={'carousel-thumbnails'}
                      aria-label={ControlsBtnLabel.replace(
                        '{key}',
                        String(key + 1)
                      ).replace('{total}', String(buttonsLength))}
                      checked={active && thumbWidth !== 0}
                      onChange={() => {
                        handleScrollTo(loop ? key + 1 : key, true);
                      }}
                      {...props}
                      className={classes([
                        'pdp-carousel__thumbnail',
                        [
                          'pdp-carousel__thumbnail--selected',
                          active && thumbWidth !== 0,
                        ],
                      ])}
                    />

                    <span
                      className={classes(['pdp-carousel__thumbnail__span'])}
                    >
                      {thumbnailSlides[key]}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {(thumbnailControls || isZoomed) && !arrowsOnMainImage && nextArrow}
        </div>
      )}
    </div>
  );
};

// Utilities
function getPerPageFromBreakpoint(
  breakpoints: { small: number; medium: number; large: number } | number,
  width = 0
) {
  if (typeof breakpoints === 'number') return breakpoints;

  const { small, medium, large } = breakpoints;
  const breakpointsMap = [
    { breakpoint: 1024, value: large },
    { breakpoint: 786, value: medium },
    { breakpoint: 0, value: small },
  ];

  const matchingBreakpoint = breakpointsMap.find(
    ({ breakpoint }) => width > breakpoint
  );

  return matchingBreakpoint ? matchingBreakpoint.value : small;
}

function isRTL() {
  return document.documentElement.dir === 'rtl';
}
